/**
 * Per-game "How to play" content. Keeps the copy in one place so the
 * onboarding dialog stays consistent in look and tone across the Play tab.
 *
 * To add a game: pick a stable `gameKey` (used as the localStorage flag
 * `eb-onboarded:<key>`), then add 3–5 short steps. Keep `body` to one or
 * two short sentences - the dialog is intentionally a quick primer, not
 * a manual.
 */

export type HowToStep = {
  title: string;
  body: string;
};

export type HowToContent = {
  /** Stable id used for the onboarding `localStorage` flag. */
  gameKey: string;
  title: string;
  /** One-line summary shown under the dialog title. */
  intro: string;
  steps: HowToStep[];
};

export const HOW_TO: Record<string, HowToContent> = {
  "element-battle": {
    gameKey: "element-battle",
    title: "How to play Element Battle",
    intro: "A 30-second tour. Best of 3 rounds, two players on one device.",
    steps: [
      {
        title: "Pick a mode",
        body: "Category - name elements from a chosen group. Or Last-letter chain - each answer must start with the last letter of the previous one.",
      },
      {
        title: "Choose a category & timer",
        body: "Categories show a difficulty rating (★) and grade band. Pick a turn timer or set it to Off to play untimed.",
      },
      {
        title: "Take turns",
        body: "Type the element name or its symbol (e.g. \"Helium\" or \"He\"). Minor typos are forgiven; repeats are not allowed.",
      },
      {
        title: "Win 2 rounds",
        body: "A round ends when a player can't answer in time or names something invalid. First to 2 round wins takes the match.",
      },
    ],
  },

  "periodic-sprint": {
    gameKey: "periodic-sprint",
    title: "How to play Periodic Sprint",
    intro: "Find the named element on the periodic table - race the clock.",
    steps: [
      {
        title: "Pick a mode",
        body: "Solo to chase your personal best, or Hot-seat 2P to compete head-to-head.",
      },
      {
        title: "Read the prompt",
        body: "You'll see an element name (and optional hint). Toggle 'show symbols' if you need help.",
      },
      {
        title: "Tap the right cell",
        body: "Click or tap the element on the grid. Number keys also work for quick navigation.",
      },
      {
        title: "Score by speed",
        body: "Answer fast for full points; slower correct answers still score; misses and timeouts score zero.",
      },
    ],
  },

  "bond-type-snap": {
    gameKey: "bond-type-snap",
    title: "How to play Bond Type Snap",
    intro: "Decide the bond type for the compound on screen as fast as you can.",
    steps: [
      {
        title: "Watch the compound",
        body: "Each round shows a chemical formula. Identify whether the bond is ionic, covalent, polar covalent, or metallic.",
      },
      {
        title: "Tap your answer",
        body: "Pick from the bond-type buttons. Number keys 1–4 also select.",
      },
      {
        title: "Mind the timer",
        body: "Faster correct answers earn more points; wrong picks lose a small amount.",
      },
      {
        title: "Beat your best",
        body: "Solo tracks your personal best. Hot-seat alternates between two players.",
      },
    ],
  },

  "ph-sort": {
    gameKey: "ph-sort",
    title: "How to play pH Sort",
    intro: "Sort substances by pH - acidic, neutral, or basic.",
    steps: [
      {
        title: "Read the substance",
        body: "Each card shows a familiar substance (lemon juice, soap, blood…) along with a hint.",
      },
      {
        title: "Pick a bucket",
        body: "Choose Acidic, Neutral, or Basic. Number keys 1–3 also work.",
      },
      {
        title: "Score speed and accuracy",
        body: "Fast correct = more points. Wrong picks deduct a little - guess strategically.",
      },
    ],
  },

  "chem-duel": {
    gameKey: "chem-duel",
    title: "How to play Chem Duel",
    intro: "Two players, one device - outscore your rival on quickfire chemistry questions.",
    steps: [
      {
        title: "Set up the duel",
        body: "Enter both nicknames. Each player gets the same set of questions.",
      },
      {
        title: "Answer fast",
        body: "Pick the right answer before the timer runs out. Faster = more points.",
      },
      {
        title: "Highest total wins",
        body: "After all questions, the player with the higher score takes the duel.",
      },
    ],
  },

  "speed-quiz": {
    gameKey: "speed-quiz",
    title: "How to play Speed Quiz",
    intro: "Race the clock through chemistry quickfire - score as many as you can.",
    steps: [
      {
        title: "Pick your length",
        body: "Choose how many questions or how much time you want - the engine builds a fresh set.",
      },
      {
        title: "Answer or skip",
        body: "Tap the right option. Skips don't lose points but use time you could be scoring.",
      },
      {
        title: "Beat your best",
        body: "Your personal best score is saved on this device.",
      },
    ],
  },

  "reaction-race": {
    gameKey: "reaction-race",
    title: "How to play Reaction Race",
    intro: "Predict reaction outcomes faster than the clock - and the other player.",
    steps: [
      {
        title: "Read the equation",
        body: "Each prompt shows reactants. You decide what the products will be.",
      },
      {
        title: "Pick the right products",
        body: "Tap the option you think balances. Faster correct answers earn more points.",
      },
      {
        title: "Most points wins",
        body: "Solo tracks your best; hot-seat awards the higher cumulative total.",
      },
    ],
  },

  "glassware-id": {
    gameKey: "glassware-id",
    title: "How to play Glassware ID",
    intro: "Identify the lab glassware shown on screen.",
    steps: [
      {
        title: "Look at the silhouette",
        body: "Each round shows a piece of common chemistry glassware.",
      },
      {
        title: "Tap its name",
        body: "Pick the right label from four options. Number keys 1–4 also select.",
      },
      {
        title: "Speed bonus",
        body: "Faster answers earn more points - but a wrong pick costs you.",
      },
    ],
  },

  "lab-safety-showdown": {
    gameKey: "lab-safety-showdown",
    title: "How to play Lab Safety Showdown",
    intro: "Spot the safe (or unsafe) practice - and learn the GHS pictograms.",
    steps: [
      {
        title: "Read the scenario",
        body: "Each card describes a lab situation or shows a hazard pictogram.",
      },
      {
        title: "Pick the right call",
        body: "Choose the safest action, or identify the pictogram. Wrong answers come with a brief explanation.",
      },
      {
        title: "Beat your best",
        body: "Solo saves your personal best. Hot-seat alternates two players.",
      },
    ],
  },

  "electron-config-sprint": {
    gameKey: "electron-config-sprint",
    title: "How to play Electron Config Sprint",
    intro: "Type the ground-state electron configuration for the element shown - race the clock.",
    steps: [
      {
        title: "Pick a difficulty",
        body: "Easy covers Z 1-20 (s and p only). Medium adds 3d and 4p. Hard includes the entire periodic table.",
      },
      {
        title: "Read the element card",
        body: "You'll see the symbol, atomic number, period, group, and block. Tap Hint to reveal just the noble-gas core.",
      },
      {
        title: "Type the configuration",
        body: "Either shorthand ([Ne] 3s² 3p¹) or full expansion (1s² 2s² 2p⁶ 3s² 3p¹). Both ² and 2 work.",
      },
      {
        title: "Score by speed",
        body: "Fast correct answers earn the full bonus. Slower correct answers still score; misses and timeouts score zero.",
      },
    ],
  },
  "chem-doku": {
    gameKey: "chem-doku",
    title: "How to play Chem-doku",
    intro: "Sudoku, but with element symbols instead of digits. Solo, no timer.",
    steps: [
      {
        title: "Pick size + pack",
        body: "Start with 4x4 to learn the rules. Pick a symbol pack (halogens, noble gases, common metals, ...) and a difficulty.",
      },
      {
        title: "Fill every cell",
        body: "Tap an empty cell, then tap a symbol below the grid to place it. Each row, column, and box must contain every symbol exactly once.",
      },
      {
        title: "Watch for conflicts",
        body: "Any cell that breaks a rule turns red. Use Erase or tap the same symbol again to clear a cell.",
      },
      {
        title: "Hint or check",
        body: "Hint reveals one correct cell. Check verifies the full board. The win dialog opens automatically when you solve it.",
      },
    ],
  },
};

export const ONBOARDED_PREFIX = "eb-onboarded:";

export function isOnboarded(gameKey: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(ONBOARDED_PREFIX + gameKey) === "1";
  } catch {
    return true;
  }
}

export function markOnboarded(gameKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ONBOARDED_PREFIX + gameKey, "1");
  } catch {
    /* ignore */
  }
}
